import SearchGradientIcon from 'ui-components/SearchGradientIcon';
import React, { useState } from "react";
import { useTheme } from "@emotion/react";
import { Box, Grid, MenuItem, Pagination, TextField, Typography, useMediaQuery } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import BreadcrumbHeader from "layout/BreadcrumbsHeader";
import ThemeBGWrapper from "ui-components/ThemeBGWrapper";
import PerfectScrollbar from "react-perfect-scrollbar";
import { containerRef } from "utils";
import { FetchAnalysisResults, FetchAnalysisReports, InviteFromAnalysis } from "api/bulk-analysis";
import AnalysisCard from "./components/AnalysisCard";
import InviteDealflowModal from "./components/InviteDealflowModal";
import { Search } from "@mui/icons-material";
import { Primary } from 'ui-components/Buttons';

const AnalysisResultsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { batch_id } = useParams();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const investorDetails = useSelector((state) => state?.investorDetails?.data);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [inviteModal, setInviteModal] = useState({ open: false, result: null });

  // Debounce search
  const searchTimeout = React.useRef(null);
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearchDebounced(value);
      setPage(1);
    }, 400);
  };

  const { data: resultsData, refetch: refetchResults } = FetchAnalysisResults(batch_id, {
    page,
    limit: 12,
    search: searchDebounced,
    status_filter: statusFilter === "all" ? "" : statusFilter,
  });

  const { data: reports } = FetchAnalysisReports(investorDetails?._id);
  const inviteMutation = InviteFromAnalysis();

  const currentReport = reports?.find((r) => r.batch_id === batch_id);

  const handleInviteSend = async (result) => {
    try {
      await inviteMutation.mutateAsync({ batch_id, file_id: result.file_id });
      setInviteModal({ open: false, result: null });
      refetchResults();
    } catch (err) {
      console.error("Failed to send invite:", err);
    }
  };

  const handleReportChange = (e) => {
    const newBatchId = e.target.value;
    if (newBatchId) {
      navigate(`/bulk-analysis/results/${newBatchId}`);
    }
  };

  return (
    <ThemeBGWrapper>
      <PerfectScrollbar containerRef={containerRef} style={{ height: "100%" }}>
        <BreadcrumbHeader
          title="Analysis Results"
          breadcrumbItems={[
            { title: "Tools", path: "" },
            { title: currentReport?.report_name || "Results" },
          ]}
        />
        <Box sx={{ padding: isSmallScreen ? "1rem" : "0 2rem 2rem 2rem" }}>
          {/* Top bar */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
              {/* Back button */}
              <Primary
                variant="outlined"
                onClick={() => navigate("/")}
                sx={{
                  textTransform: "none",
                  backgroundColor: "transparent",
                  color: theme.palette.customColors.g200,
                  border: `1px solid ${theme.palette.customColors.gray300}`,
                  "&:hover": { backgroundColor: theme.palette.customColors.inputField },
                  minWidth: "auto",
                  padding: "6px 12px",
                }}
              >
                &larr;
              </Primary>

              {/* Search */}
              <TextField
                size="small"
                placeholder="Search startups by deck name"
                value={search}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: <SearchGradientIcon />,
                }}
                sx={{
                  minWidth: 280,
                  flex: 1,
                  maxWidth: 400,
                  "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: "#fff" },
                }}
              />

              {/* Status filter */}
              <TextField
                select
                size="small"
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                sx={{
                  minWidth: 140,
                  "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: "#fff" },
                }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pass">Pass</MenuItem>
                <MenuItem value="fail">Fail</MenuItem>
              </TextField>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              {/* Start Over */}
              <Primary
                onClick={() => navigate("/bulk-analysis")}
                sx={{ textTransform: "none" }}
              >
                Start over
              </Primary>

              {/* Report selector */}
              {reports && reports.length > 0 && (
                <TextField
                  select
                  size="small"
                  value={batch_id}
                  onChange={handleReportChange}
                  sx={{
                    minWidth: 140,
                    "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: "#fff" },
                  }}
                >
                  {reports.map((r) => (
                    <MenuItem key={r.batch_id} value={r.batch_id}>
                      {r.report_name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Box>
          </Box>

          {/* Results grid */}
          {resultsData?.results?.length > 0 ? (
            <>
              <Grid container spacing={2.5}>
                {resultsData.results.map((result) => (
                  <Grid item xs={12} sm={6} md={4} key={result.file_id}>
                    <AnalysisCard
                      result={result}
                      onInvite={(r) => setInviteModal({ open: true, result: r })}
                    />
                  </Grid>
                ))}
              </Grid>

              {/* Pagination */}
              {resultsData.total_pages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                  <Pagination
                    count={resultsData.total_pages}
                    page={page}
                    onChange={(_, p) => setPage(p)}
                    color="primary"
                    shape="rounded"
                  />
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="body1" sx={{ color: theme.palette.customColors.g200 }}>
                {searchDebounced || statusFilter !== "all"
                  ? "No results match your search or filter."
                  : "No results yet. Start an analysis to see results here."}
              </Typography>
            </Box>
          )}
        </Box>

        <InviteDealflowModal
          open={inviteModal.open}
          onClose={() => setInviteModal({ open: false, result: null })}
          result={inviteModal.result}
          onSend={handleInviteSend}
          isLoading={inviteMutation.isLoading}
        />
      </PerfectScrollbar>
    </ThemeBGWrapper>
  );
};

export default AnalysisResultsPage;
